import React, { ReactElement, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Paper, { PaperProps } from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Draggable from 'react-draggable';

import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import { PsfTemplateItemWithCount } from '../../../../@types/occurrence-dna';

interface PsfCountTableProps {
    open: boolean;
    onClose: () => void;
    env: CanvasEnvironment | null;
}

function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

interface Column {
    id: 'category' |  'name' | 'code' | 'description'  | 'count' ;
    label: string;
    minWidth?: number;
    align?: 'right';
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { id: 'category', label: 'Category', minWidth: 100 },
    { id: 'name', label: 'Name', minWidth: 100 },
    { id: 'code', label: 'Code', minWidth: 100 },
    { id: 'description', label: 'Description', minWidth: 200 },
    { id: 'count', label: 'Count', minWidth: 170 },
];


const PsfCountTableModal: React.FC<PsfCountTableProps> = ({ open, onClose, env }) => {
    const [psfInfoCount, setPsfInfoCount] = useState<PsfTemplateItemWithCount[]>([]);

    useEffect(() => {
        if (open && env) {
            const [selectedNode] = env!.selection
            if (selectedNode) {
                setPsfInfoCount(env.psfCounts.get(selectedNode.get('data').id) || []);
            }
        }
    }, [open, env]);

    return (
        <Dialog open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="md">
            <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>Performance Shaping Factors (PSF) Counting Table</DialogTitle>
            <DialogContent dividers >
                <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto'}}>
                    <Table stickyHeader size='small'>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {psfInfoCount.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.category}</TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.code}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>{row.count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PsfCountTableModal;